import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';

// Initialize Sentry before anything else
// DSN can be set via environment variable, with hardcoded fallback for APK builds
// (DSN is not a secret - it's safe to include in client code)
const SENTRY_DSN =
  import.meta.env.VITE_SENTRY_DSN ||
  'https://dbd5200774cfdb632db3c0809fa79b74@o4510762419814400.ingest.us.sentry.io/4510767423946752';
const APP_VERSION = import.meta.env.VITE_APP_VERSION || 'unknown';
const ENV_MODE = import.meta.env.MODE;

console.log('[Sentry] Checking configuration...');
console.log('[Sentry] DSN configured:', !!SENTRY_DSN);
console.log('[Sentry] DSN prefix:', SENTRY_DSN ? SENTRY_DSN.substring(0, 30) + '...' : 'none');
console.log('[Sentry] Environment:', ENV_MODE);
console.log('[Sentry] App version:', APP_VERSION);

if (SENTRY_DSN) {
  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: ENV_MODE, // 'development' or 'production'
      release: `uliver-mobile@${APP_VERSION}`,

      // Debug mode logs every span/transaction to console - enable only when debugging Sentry itself
      debug: false,

      // Send default PII data (IP address, etc.)
      sendDefaultPii: true,

      // Capture 100% of errors in production
      sampleRate: 1.0,

      // Performance monitoring - capture transactions for tracing
      // TODO: Lower to 0.02 after testing to stay within free plan limits
      tracesSampleRate: 1.0,

      // Distributed tracing - propagate trace headers to backend
      tracePropagationTargets: ['localhost', /^https:\/\/uliver\.aryeh\.win/, /^https:\/\/.*\.uliver\.co\.il/],

      // Integrations for performance monitoring and breadcrumbs
      integrations: [
        // Browser tracing for performance monitoring (page loads, navigation, requests)
        Sentry.browserTracingIntegration({
          idleTimeout: 5000, // Wait 5s after last activity before finishing idle span (default 1s)
        }),
        // Capture console.log/warn/error as breadcrumbs (shows what happened before crash)
        Sentry.breadcrumbsIntegration({
          console: true, // Capture console.* calls
          dom: true, // Capture click/input events
          fetch: true, // Capture fetch requests
          history: true, // Capture navigation
          xhr: true, // Capture XHR requests
        }),
      ],

      // Filter out noisy errors
      beforeSend(event, hint) {
        console.log('[Sentry] beforeSend - sending event:', event.event_id);
        // Ignore chunk loading errors (handled by app reload)
        const error = hint.originalException;
        if (error instanceof Error && error.message.includes('Failed to fetch dynamically imported module')) {
          console.log('[Sentry] Filtered out chunk loading error');
          return null;
        }
        return event;
      },

      // Add extra context
      initialScope: {
        tags: {
          platform: typeof (window as any).Capacitor !== 'undefined' ? 'capacitor' : 'web',
        },
      },
    });

    // Verify Sentry is initialized
    const client = Sentry.getClient();
    console.log('[Sentry] Client initialized:', !!client);
    console.log('[Sentry] Client DSN:', client?.getDsn()?.toString().substring(0, 30) + '...');

    // Send a test message to verify Sentry is working
    Sentry.captureMessage('[Sentry] App initialized - test message', 'info');
    console.log('[Sentry] Test message sent');

    console.log('[Sentry] Initialized successfully for error tracking');

    // Expose test functions globally for debugging (accessible from console)
    (window as any).sentryTest = {
      // Send a test error with full stack trace
      sendError: () => {
        try {
          throw new Error('[Test] Sample error from Android - ' + new Date().toISOString());
        } catch (e) {
          Sentry.captureException(e);
          console.log('[Sentry] Test error sent!');
        }
      },
      // Send a test message
      sendMessage: (msg?: string) => {
        Sentry.captureMessage(msg || '[Test] Sample message from Android - ' + new Date().toISOString(), 'info');
        console.log('[Sentry] Test message sent!');
      },
      // Send a warning
      sendWarning: (msg?: string) => {
        Sentry.captureMessage(msg || '[Test] Sample warning from Android - ' + new Date().toISOString(), 'warning');
        console.log('[Sentry] Test warning sent!');
      },
    };
    console.log('[Sentry] Test functions available: window.sentryTest.sendError(), .sendMessage(), .sendWarning()');
  } catch (initError) {
    console.error('[Sentry] Failed to initialize:', initError);
  }
} else {
  console.log('[Sentry] No DSN configured, error tracking disabled');
  console.log('[Sentry] To enable, set VITE_SENTRY_DSN in .env file');
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Could not find root element to mount to');
}

// Show loading state while waiting for Capacitor
rootElement.innerHTML =
  '<div style="display:flex;align-items:center;justify-content:center;height:100vh;background:#E0F7FA;font-family:Nunito,sans-serif;color:#0097A7;font-size:24px;">Loading Uliver...</div>';

// Wait for Capacitor to be ready before importing App (which has Capacitor plugin imports)
const initApp = async () => {
  try {
    // Give Capacitor time to initialize on mobile
    if (typeof window !== 'undefined' && (window as any).Capacitor) {
      console.log('[Init] Waiting for Capacitor...');
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    // Dynamically import App after Capacitor is ready
    const { default: App } = await import('./App');

    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <React.StrictMode>
        <Sentry.ErrorBoundary
          fallback={({ error, resetError }) => (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                background: '#E0F7FA',
                padding: '20px',
                textAlign: 'center',
                fontFamily: 'Nunito, sans-serif',
              }}
            >
              <div style={{ color: '#d32f2f', fontSize: '20px', marginBottom: '16px' }}>Something went wrong</div>
              <div style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
                {error?.message || 'An unexpected error occurred'}
              </div>
              <button
                onClick={resetError}
                style={{
                  padding: '12px 24px',
                  background: '#4DD0E1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  cursor: 'pointer',
                }}
              >
                Try Again
              </button>
            </div>
          )}
          onError={(error, componentStack) => {
            console.error('[Sentry] React Error Boundary caught error:', error);
            console.error('[Sentry] Component Stack:', componentStack);
          }}
        >
          <App />
        </Sentry.ErrorBoundary>
      </React.StrictMode>,
    );
  } catch (error) {
    console.error('[Init] Failed to load app:', error);
    rootElement.innerHTML = `<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;background:#E0F7FA;padding:20px;text-align:center;font-family:Nunito,sans-serif;">
      <div style="color:#d32f2f;font-size:20px;margin-bottom:16px;">Failed to load app</div>
      <div style="color:#666;font-size:14px;">${error instanceof Error ? error.message : String(error)}</div>
      <button onclick="location.reload()" style="margin-top:20px;padding:12px 24px;background:#4DD0E1;color:white;border:none;border-radius:8px;font-size:16px;cursor:pointer;">Retry</button>
    </div>`;
  }
};

initApp();

// Register service worker for PWA (only in production web, not in Capacitor or development)
// In development, sw.js doesn't exist and trying to register it causes errors
if ('serviceWorker' in navigator && !(window as any).Capacitor && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    // Track if we've already triggered a reload this session to prevent infinite loops
    const SW_RELOAD_KEY = 'sw_reload_pending';
    const SW_VERSION_KEY = 'sw_active_version';

    // Check if we just reloaded - clear the flag
    if (sessionStorage.getItem(SW_RELOAD_KEY)) {
      console.log('[PWA] Page reloaded after SW update, clearing reload flag');
      sessionStorage.removeItem(SW_RELOAD_KEY);
    }

    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        console.log('[PWA] Service Worker registered:', registration.scope);

        // Check for updates every 60 seconds (1 minute)
        setInterval(() => {
          console.log('[PWA] Checking for updates...');
          registration.update().catch((error) => {
            // Network errors during update checks are expected (offline, etc.)
            console.log('[PWA] Update check failed:', error.message);
          });
        }, 60000);

        // Listen for service worker updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          console.log('[PWA] New version found, installing...');

          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New service worker installed, will activate soon
                console.log('[PWA] New version installed, will reload on activation');
              }
            });
          }
        });
      })
      .catch((error) => {
        console.log('[PWA] Service Worker registration failed:', error);
      });

    // Listen for messages from the service worker
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'SW_UPDATED') {
        const newVersion = event.data.version;
        const lastVersion = localStorage.getItem(SW_VERSION_KEY);
        console.log('[PWA] Service Worker reports version:', newVersion, 'Last known:', lastVersion);

        // Only reload if this is actually a new version AND we haven't already triggered a reload
        if (newVersion !== lastVersion && !sessionStorage.getItem(SW_RELOAD_KEY)) {
          console.log('[PWA] New version detected! Reloading...');
          localStorage.setItem(SW_VERSION_KEY, newVersion);
          sessionStorage.setItem(SW_RELOAD_KEY, 'true');
          setTimeout(() => {
            window.location.reload();
          }, 100);
        } else {
          console.log('[PWA] Same version or reload already pending, skipping reload');
        }
      }
    });
  });
}
